<template>
  <div class="qb-video-list-wrapper">
    <ul
      v-if="Array.isArray(shortVideo) && shortVideo.length"
      class="qb-video-list"
    >
      <li
        v-for="(item, index) in shortVideo"
        :id="`qb-video-item-${index}`"
        :key="index"
        class="qb-video-item"
        :data-report="index"
        data-exposed="false"
        :ourl="/^\/play/g.test(item.url) ? `https://wapv.sogou.com${item.url}` : item.url"
        @click="clickOutResult($event, item, index)"
      >
        <h3
          class="qb-video-title"
          v-html="item.titleCut"
        />
        <div class="qb-video-content">
          <video
            v-if="supportAutoplay && videoExposedArr.includes(index)"
            :ref="`video-${index}`"
            class="video-player"
            :data-index="index"
            :data-type="item.videoCdn ? 'play' : item.videoMp4 ? 'scan' : ''"
            :src="item.videoCdn || item.videoMp4"
            :muted="mute"
            width="100%"
            height="100%"
            playsinline
            webkit-playsinline
            x5-video-player-type="h5-page"
            @progress="handleVideoFirstPlay"
            @loadeddata="handleVideoFirstPlay"
            @playing="handleVideoPlaying"
            @timeupdate="handleVideoUpdate"
            @pause="handleVideoPause"
            @ended="handleVideoEnd"
            @error="handleVideoError"
          />
          <!-- 封面图 -->
          <img
            v-if="index < 3"
            :id="`video_poster_${index}`"
            :src="item.currentTime > 0 ? '' : item.picurl"
            referrerpolicy="no-referrer"
          >
          <img
            v-else
            :id="`video_poster_${index}`"
            :src="item.currentTime > 0 ? '' : item.picurl"
            loading="lazy"
            referrerpolicy="no-referrer"
            onerror="this.style.display='none'"
          >
          <!-- 播放状态 -->
          <span
            v-if="item.playStatus === PLAY_STATE.ERROR && supportAutoplay"
            class="status-to-play"
          >点击播放</span>
          <QBVideoLoading
            v-else-if="item.playStatus === PLAY_STATE.LOADING && index === activeVideoIndex"
            @loadingTimeout="handleLoadingTimeout(index)"
            @loadingPolling="handleLoadingPolling(item, index)"
          />
          <span v-else-if="item.playStatus === PLAY_STATE.PLAYING && index === activeVideoIndex" />
          <span
            v-else
            class="status-pause"
          />
          <!-- 合集标签 -->
          <span
            v-if="item.showTagName === '合集' && item.showTagDesc"
            class="collection-desc"
          >
            {{ item.showTagDesc }}
          </span>
          <!-- 播放时长 -->
          <div
            v-if="activeVideoIndex === index && item.videoCdn"
            class="duration"
          >
            {{ formatDuration(item) }}
          </div>
          <div
            v-else-if="activeVideoIndex === index && item.videoMp4 && item.playedTimes === 0"
            class="duration"
          >
            {{ `片段 ${formatDuration(item)}` }}
          </div>
          <div
            v-else-if="activeVideoIndex === index && item.videoMp4 && item.playedTimes > 0"
            class="duration duration-scan-end"
          >
            {{ `看完整视频${item.duration}` }}<i class="icon-right-arrow" />
          </div>
          <div
            v-else
            class="duration"
          >
            {{ item.duration }}
          </div>
          <!-- 音标 -->
          <a
            v-if="activeVideoIndex === index && item.videoCdn && item.currentTime > 0"
            class="icon-voice"
            href="javascript:void(0);"
            :uigs="`video_voice_${mute ? 'mute' : 'play'}`"
            :uigsparams="getShortVideoPlayInfo({ item, index })"
            @click="toggleVoice"
          >
            <i :class="`icon-voice-${mute ? 'mute' : 'open'}`" />
          </a>
          <span
            v-if="activeVideoIndex === index && item.videoCdn && item.currentTime > 0"
            class="video-player-progress"
          >
            <i
              class="video-player-progress-played"
              :style="`width: ${formatPercent(item)}%;`"
            />
          </span>
        </div>
        <div
          class="qb-video-detail"
          @click.stop
        >
          <SiteIcon
            :icon-img="item.siteicon"
            :icon-type="item.iconType"
            :is-lazy="index > 2"
          />
          <span
            v-if="item.sitename"
            class="video-detail-sitename"
          >{{ item.sitename }}</span>
          <span
            v-if="item.date"
            class="video-detail-date"
          >{{ item.date }}</span>
        </div>
        <div
          style="display: none"
          v-html="'<!--v-->'"
        />
      </li>
    </ul>
    <div
      v-if="!loadingFinished"
      class="loading-result"
    />
  </div>
</template>

<script>
import { debounce } from '@asset/js/util';
import * as types from '@types';
import smoothscroll from 'smoothscroll-polyfill';
import { mapMutations, mapState } from 'vuex';

import SiteIcon from '../../common/components/SiteIcon';
import QBVideoLoading from './QBVideoLoading.vue';
const URL = require('@tencent/search-url');

// 视频播放状态
const PLAY_STATE = {
  // 无资源或加载失败均对应该状态
  ERROR: -1,
  LOADING: 0,
  PLAYING: 1,
  PAUSE: 2,
};

export default {
  components: {
    SiteIcon,
    QBVideoLoading,
  },
  props: {
    getDocInfo: {
      type: Function,
      default: () => null,
    },
    getShortVideoPlayInfo: {
      type: Function,
      default: () => null,
    },
  },
  data() {
    return {
      // 触摸滚动的方向
      touchDirection: 'up',
      // 在播视频实例
      videoInstance: null,
      // 被驱动播放视频索引，并不代表视频已真实开始播放
      activeVideoIndex: -1,
      // 静音
      mute: true,
      // 记录每个video的顶底位置
      yPosArr: [],
      // 记录已经被曝光的视频索引
      videoExposedArr: [0],
      // 记录当前被曝光的视频索引
      videoCurExposedArr: [],
      // 触摸锁
      touchLock: false,
      // 是否支持自动播放
      supportAutoplay: false,
      // 视频播放状态
      PLAY_STATE,
    };
  },
  computed: {
    ...mapState('common', ['loadingFinished', '$config']),
    ...mapState('search', ['shortVideo']),
    terminal() {
      let { userAgent = '' } = this.$config || {};
      userAgent = userAgent.toLowerCase();
      let terminal = 'other';
      if (/(?:iphone|ipad|ipod)/gi.test(userAgent)) {
        terminal = 'ios';
      }
      if (userAgent.includes('android') || userAgent.includes('adr')) {
        terminal = 'android';
      }
      return terminal;
    },
  },
  watch: {
    videoCurExposedArr(newArr, oldArr) {
      // 无曝光video，停止所有播放
      if (!newArr.length) {
        this.videoInstance && this.videoInstance.pause();
        this.activeVideoIndex = -1;
        return;
      }
      const isSameExposed = newArr.length === oldArr.length && newArr[0] === oldArr[0];
      const videoToPlay = this.decideWhichToPlay(newArr, isSameExposed);
      const videoToPlayIndex = newArr[videoToPlay];
      // 无可播video或可播video正在播放
      if (videoToPlayIndex < 0 || videoToPlayIndex === this.activeVideoIndex) {
        return;
      }
      const [videoRef] = this.$refs[`video-${videoToPlayIndex}`] || [];
      videoRef && this.playVideo(videoRef);
    },
    shortVideo(newArr, oldArr) {
      if (newArr.length > oldArr.length) {
        // 等待懒加载项渲染
        window.setTimeout(() => {
          this.updateYPosArr(oldArr.length, newArr.length - 1);
        }, 50);
      }
    },
  },
  async mounted() {
    this.supportAutoplay = await this.getSupportAutoplay();
    if (!this.supportAutoplay) return;

    // 兼容ios平滑滚动
    smoothscroll.polyfill();
    this.updateYPosArr(0, this.shortVideo.length - 1);
    const videoCurExposed = this.calcExposedIndex();
    this.videoExposedArr = this.arrMerge(this.videoExposedArr, videoCurExposed);
    window.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.debouncedScrollHandler = debounce(this.handleScroll, 300);
    window.addEventListener('scroll', this.debouncedScrollHandler);
    window.addEventListener('touchstart', this.handleTouchStart);
    window.addEventListener('touchend', this.handleTouchEnd);

    // 无v_record_index, 说明是mouted触发的播放逻辑
    if (!window.sessionStorage || !window.sessionStorage.getItem('v_record_index')) {
      this.handleVideoExposed();
    }
    window.sessionStorage && window.sessionStorage.removeItem('v_record_index');
  },
  beforeDestroy() {
    window.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('scroll', this.debouncedScrollHandler);
    window.removeEventListener('touchstart', this.handleTouchStart);
    window.removeEventListener('touchend', this.handleTouchEnd);
  },
  methods: {
    ...mapMutations('search', [
      types.UPDATE_SEARCH_VIDEO_PLDTIMES,
      types.UPDATE_SEARCH_VIDEO_PLDPROGRESS,
      types.UPDATE_SEARCH_VIDEO_STATUS,
    ]),
    /**
     * 获取当前网络状态
     */
    getSupportAutoplay() {
      return new Promise((resolve, reject) => {
        const { platform, connection } = window.navigator || {};
        // chrome模拟器
        if (platform === 'MacIntel' || platform === 'Win32') return resolve(true);
        // 兼容navigator.connection
        if (connection && connection.type === 'wifi') return resolve(true);
        // QB jsapi
        const { connection: QBConnection } = window.browser || {};
        if (QBConnection && typeof QBConnection.getType === 'function') {
          QBConnection.getType(type => resolve(type === 'wifi'), () => reject(false));
        } else {
          reject(false);
        }
      });
    },
    /**
     * 更新每个video的顶底y坐标
     * @param {Number} start 计算坐标的起始索引
     * @param {Number} end 计算坐标的结束索引
     */
    updateYPosArr(start, end) {
      const $videoList = document.querySelectorAll('.qb-video-content');
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      ($videoList || []).forEach((item, index) => {
        if (start <= index && index <= end) {
          const { top, bottom } = item.getBoundingClientRect();
          // 此处保存的是相对于列表容器的位置
          this.yPosArr[index] = [scrollTop + top, scrollTop + bottom];
        }
      });
    },
    /**
     * 计算当前视口中展现video的索引
     */
    calcExposedIndex() {
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      const scrollBottom = scrollTop + window.innerHeight;
      return this.yPosArr.reduce((acc, item, index) => {
        const inScreen = scrollTop < item[0] && scrollBottom > item[1];
        // 兼容切tag时，坐标数组和video长度不匹配
        const { videoType } = this.shortVideo[index] || {};
        if (inScreen && videoType) {
          acc.push(index);
        }
        return acc;
      }, []);
    },
    /**
     * 数组去重合并
     */
    arrMerge(arr1 = [], arr2 = []) {
      return Array.from(new Set([...arr1, ...arr2]));
    },
    clickOutResult($event, item, index) {
      const { className } = $event.target || {};
      if (className.includes('icon-voice')) return;

      // 点击上报
      let uigsCl = '';
      if (item.posName) {
        uigsCl = `${item.posName}_${item.location}_${item.id}_${item.title}`;
      } else {
        uigsCl = `out_list_video_${index}|${item.docid}|${item.itemUrl}`;
      }
      const baseUigsParams = {
        ...window.UIGS_PARA,
        play_type: 'video',
        play_url: encodeURIComponent(item.url),
        play_site: item.site_d || '',
        ...this.getDocInfo(item, index),
        pbtype: 'cl',
        pagetype: 'search_result',
        uigs_cl: uigsCl,
      };

      if (item.resType === 'native') {
        window.sessionStorage && window.sessionStorage.setItem('v_record_index', index);
        const timeNow = Date.now();
        const { uuid = '' } = window.UIGS_PARA;
        const clickSessionId = uuid ? `${uuid}_${timeNow}` : '';
        const playStartTime = item.currentTime > 0 ? (item.currentTime * 1000).toFixed(0) : 0;

        // 自动播放上报
        const autoplayReport = item.videoType ? this.getDurationInfo(item) : {};
        this.$uigs.send({
          ...baseUigsParams,
          ...autoplayReport,
          clickSessionId,
        });

        if (item.resSType === 'na_ugcfloat') {
          let ugcFloatUrl = `${item.url}&clickTimeStamp=${timeNow}&clickSessionId=${clickSessionId}`;
          const { videoCdn = '' } = item;
          // 过期时间为获取时间后4小时
          if (videoCdn) {
            const { dis_t: disT } = URL.parse(videoCdn).query || {};
            const time30MinAgo = ((timeNow / 1000) - 30 * 60).toFixed(0);
            const expiretime = disT ? parseInt(disT, 10) + 4 * 60 * 60 : time30MinAgo;
            ugcFloatUrl += `&videoUrl=${encodeURIComponent(videoCdn)}&expiretime=${expiretime}`;
          }
          if (playStartTime) {
            ugcFloatUrl += `&playFromTime=${playStartTime}`;
          }
          window.location.href = ugcFloatUrl;
        } else {
          const clickOutParams = {
            clickSessionId,
            jumpStartTime: timeNow,
            useShell: 1,
            preloadShell: 1,
            nafrom: 'video',
            bvEnd: 90,
            continue: 1,
            playStartTime: +playStartTime,
          };
          const clickOutParamsStr = Object.keys(clickOutParams).reduce((acc, key) => `${acc}&${key}=${clickOutParams[key]}`, '');
          window.location.href = item.url + clickOutParamsStr;
        }
      } else {
        this.$uigs.send(baseUigsParams);
        window.location.href = item.url;
      }
    },
    handleTagChange() {
      if (!this.supportAutoplay) return;
      const videoPoster = document.querySelector(`#video_poster_${this.activeVideoIndex}`);
      videoPoster && (videoPoster.style.display = 'block');
      this.handleScroll();
      this.updateYPosArr(0, this.shortVideo.length - 1);
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        if (window.sessionStorage && window.sessionStorage.getItem('v_record_index')) {
          const localIndex = parseInt(window.sessionStorage.getItem('v_record_index'), 10);
          // ios非静音状态下，不支持自动播放
          if (this.terminal !== 'ios' || this.mute) {
            const timer = window.setTimeout(() => {
              const videoCurExposed = this.calcExposedIndex();
              if (videoCurExposed.includes(localIndex)) {
                const [videoItem] = this.$refs[`video-${localIndex}`];
                videoItem && this.playVideo(videoItem);
              } else {
                const [videoItem] = this.$refs[`video-${videoCurExposed[0]}`];
                videoItem && this.playVideo(videoItem);
              }
              window.clearTimeout(timer);
            }, 200);
          }
        }
        window.sessionStorage && window.sessionStorage.removeItem('v_record_index');
      }
      if (document.visibilityState === 'hidden') {
        this.videoInstance && this.videoInstance.pause();
      }
    },
    handleScroll() {
      !this.touchLock && this.handleVideoExposed();
    },
    handleTouchStart($event) {
      this.touchLock = true;
      this.touchY0 = $event.touches[0].clientY;
    },
    handleTouchEnd($event) {
      this.touchLock = false;
      const touchY1 = $event.changedTouches[0].clientY;
      const distance = touchY1 - this.touchY0;
      if (Math.abs(distance) < 1) return;
      this.touchDirection = distance < 0 ? 'up' : 'down';
      this.handleVideoExposed();
    },
    handleVideoExposed() {
      const videoCurExposed = this.calcExposedIndex();
      this.videoExposedArr = this.arrMerge(this.videoExposedArr, videoCurExposed);
      // 延迟50ms，等待video标签渲染
      window.setTimeout(() => {
        this.videoCurExposedArr = [...videoCurExposed];
      }, 50);
    },
    /**
     * 计算视口中的video该播哪个
     * 1.触顶/触底优先播顶/底
     * 2.曝光video未发生变化，无动作
     * 3.正常情况，上滑播第一个，下滑播最后一个
     */
    decideWhichToPlay(arr, isSame) {
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
      const isToggleTop = scrollTop === 0;
      // 兼容触底计算误差，触底时scrollTop为小数
      const isToggleBottom = scrollHeight - scrollTop - window.innerHeight < 1;
      let video2Play;
      if (isToggleTop) {
        video2Play = 0;
      } else if (isToggleBottom) {
        video2Play = arr.length - 1;
      } else if (isSame) {
        return -1;
      } else {
        video2Play = this.touchDirection === 'up' ? 0 : arr.length - 1;
      }
      return video2Play;
    },
    /**
     * 更新视频的播放状态
     */
    updateVideoStatus(index, status) {
      if (this.shortVideo[index]) {
        this[types.UPDATE_SEARCH_VIDEO_STATUS]({ index, status });
      }
    },
    playVideo(videoRef) {
      if (this.videoInstance && this.videoInstance !== videoRef) {
        this.videoInstance.pause();
      }
      this.videoInstance = videoRef;
      const { index } = videoRef.dataset;
      this.activeVideoIndex = parseInt(index, 10);
      // 解决安卓播放第二遍时无法重新读秒的问题
      const item = this.shortVideo[index] || {};
      if (this.terminal === 'android' && item.currentTime === 0 && item.playedTimes > 0) {
        this.videoInstance.load();
      }
      if (videoRef.readyState > 0 && item.playStatus !== PLAY_STATE.PLAYING) {
        this.videoInstance.play();
        this.updateVideoStatus(index, PLAY_STATE.PLAYING);
      }
    },
    /**
     * video主动驱动第一次播放
     * ios: video加载会触发两次progress事件
     * android: 等loadeddata即开始主动播放
     */
    handleVideoFirstPlay($event) {
      const $videoItem = $event.target;
      const iIndex = parseInt($videoItem.dataset.index, 10);
      if (
        this.activeVideoIndex === iIndex
        && $videoItem.readyState > 0
        && this.shortVideo[iIndex]
        && this.shortVideo[iIndex].playStatus === PLAY_STATE.LOADING
      ) {
        $videoItem.play();
        this.updateVideoStatus(iIndex, PLAY_STATE.PLAYING);
      }
    },
    /**
     * video真实播放事件
     */
    handleVideoPlaying($event) {
      const index = $event.target && $event.target.dataset.index;
      // 兼容非手动触发play方法引发的视频播放，三角图标展现
      this.updateVideoStatus(index, PLAY_STATE.PLAYING);
    },
    /**
     * video更新事件
     */
    handleVideoUpdate($event) {
      const $videoItem = $event.target;
      const { readyState, duration, currentTime, dataset } = $videoItem;
      const { index } = dataset;
      if (+index >= 0 && readyState > 0 && duration > 0 && currentTime >= 0) {
        this[types.UPDATE_SEARCH_VIDEO_PLDPROGRESS]({
          index,
          currentTime,
          duration,
        });
      }
    },
    /**
     * video暂停事件
     */
    handleVideoPause($event) {
      const $videoItem = $event.target;
      const { index, type } = $videoItem.dataset;
      const iIndex = parseInt(index, 10);
      if (type !== 'scan' || iIndex !== this.activeVideoIndex) {
        this.updateVideoStatus(index, PLAY_STATE.PAUSE);
      }
      // 上报播放时长
      const item = this.shortVideo[iIndex];
      this.$uigsKd.send({
        ...window.UIGS_PARA,
        ...this.getDocInfo(item, index),
        ...this.getDurationInfo(item),
        pagetype: 'duration_report',
      });
    },
    /**
     * video结束事件
     */
    handleVideoEnd() {
      if (this.videoInstance) {
        const { type, index } = this.videoInstance.dataset;
        const index2Int = parseInt(index, 10);
        this[types.UPDATE_SEARCH_VIDEO_PLDTIMES](index2Int);
        if ((index < this.shortVideo.length - 1) && type === 'play') {
          const video2Play = document.querySelector(`#qb-video-item-${index2Int + 1}`);
          this.touchDirection = 'up';
          video2Play.scrollIntoView({ behavior: 'smooth' });
          const timer = window.setTimeout(() => {
            this[types.UPDATE_SEARCH_VIDEO_PLDPROGRESS]({ index, currentTime: 0 });
            const videoPoster = document.querySelector(`#video_poster_${index}`);
            videoPoster && (videoPoster.style.display = 'block');
            timer && window.clearTimeout(timer);
          }, 300);
        }
        if (type === 'scan') this.videoInstance.play();
      }
    },
    handleVideoError($event) {
      const $videoItem = $event.target;
      const { index } = $videoItem.dataset;
      this.updateVideoStatus(index, PLAY_STATE.ERROR);
    },
    // loading超时
    handleLoadingTimeout(index) {
      this.updateVideoStatus(index, PLAY_STATE.ERROR);
    },
    // loading轮询
    handleLoadingPolling(item, index) {
      if (
        item.playStatus === PLAY_STATE.LOADING
        && index === this.activeVideoIndex
        && this.videoInstance
        && this.videoInstance.readyState > 0
      ) {
        this.videoInstance.play();
        this.updateVideoStatus(index, PLAY_STATE.PLAYING);
      }
    },
    toggleVoice() {
      this.mute = !this.mute;
    },
    getDurationInfo(item) {
      return {
        // 播放类型
        video_type: item.videoType,
        // 视频总时长
        video_duration: item.totalDuration && item.totalDuration.toFixed(2),
        // 视频重播遍数
        video_replay_times: item.playedTimes,
        // 视频当前已播时间
        video_current_played: item.currentTime ? item.currentTime.toFixed(2) : 0,
      };
    },
    formatDuration(item) {
      const { currentTime, totalDuration, duration } = item;
      if (currentTime >= 0 && totalDuration > 0) {
        const remainDuration = totalDuration - currentTime;
        const minute = Math.floor(remainDuration / 60).toString();
        const second = Math.floor(remainDuration % 60).toString();
        const minuteStr = minute.length === 1 ? `0${minute}` : minute;
        const secondStr = second.length === 1 ? `0${second}` : second;
        return `${minuteStr}:${secondStr}`;
      }
      return duration;
    },
    formatPercent(item) {
      const { currentTime, totalDuration } = item;
      if (currentTime > 0 && totalDuration > 0) {
        return (currentTime / totalDuration) * 100;
      }
      return 0;
    },
  },
};
</script>

<style lang="scss">
@import '../../../asset/css/abstracts/mixin.scss';
@import '../../../asset/css/abstracts/function_sprite_search.scss';
@import '../../../asset/css/abstracts/function_sprite_qbresult.scss';

.qb-video-list-wrapper {
  margin: 4px 16px 0 16px;

  .qb-video-list {
    .qb-video-item {
      display: flex;
      flex-direction: column;
      border-bottom: 1px solid #eeeeee;
      padding: 12px 0 10px 0;
      scroll-behavior:smooth;
      &:first-child {
        padding-top: 6px;
      }

      &:last-child {
        border-bottom: none;
      }

      .qb-video-title {
        overflow: hidden;
        font-size: 17px;
        color: rgba(36,36,36,1);
        line-height: 24px;
        font-family: "PingFang SC";
        margin-bottom: 6px;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        display: -webkit-box;
      }

      .qb-video-content {
        width: 100%;
        padding-top: 56.25%;
        overflow: hidden;
        flex-grow: 0;
        flex-shrink: 0;
        position: relative;
        border-radius: 12px;
        box-sizing: border-box;
        background: #EBEDEF;
        z-index: 2;

        .video-player {
          position: absolute;
          top: 0;
          left: 0;
          border-radius: 12px;
        }

        img {
          height: 100%;
          width: 100%;
          position: absolute;
          top: 0;
          left: 0;
        }

        img[src=""], img:not([src]) {
          opacity: 0;
        }

        .icon-voice {
          position: absolute;
          bottom: 9px;
          left: 9px;
          width: 30px;
          height: 30px;
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1;
          background: rgba(0, 0, 0, 0.5);
          border-radius: 15px;

          &-mute, &-open {
            position: absolute;
          }

          &-mute {
            @include sprite-item('qbresult/mute');
          }

          &-open {
            @include sprite-item('qbresult/volumn');
          }
        }

        .duration {
          position: absolute;
          font-family: "PingFang SC";
          right: 9px;
          bottom: 9px;
          font-size: 12px;
          color: #fff;
          line-height: 18px;
          z-index: 1;
          background: rgba(0, 0, 0, 0.5);
          border-radius: 9px;
          height: 18px;
          text-align: center;
          padding: 0 8px;
          display: flex;
          align-items: center;

          &.duration-scan-end {
            padding: 0 5px;

            .icon-right-arrow {
              display: inline-block;
              margin-left: 4px;
              @include sprite-item('qbresult/right_arrow');
            }
          }
        }

        .status-to-play {
          @extend %-absolute-center;
          width: 110px;
          height: 40px;
          border-radius: 20px;
          font-size: 14px;
          color: white;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;

          &:before {
            content: '';
            position: relative;
            display: inline-block;
            margin-right: 5px;
            @include sprite-item('qbresult/play_tiny');
          }
        }

        .status-pause {
          content: '';
          z-index: 1;
          @include sprite-item('qbresult/play');
          @extend %-absolute-center;
        }

        .collection-desc {
          position: absolute;
          left: 0;
          top: 0;
          z-index: 1;
          display: flex;
          align-items: center;
          height: 20px;
          line-height: 20px;
          font-size: 12px;
          font-family: 'PingFang SC';
          color: #fff;
          background: rgba(0,0,0,1);
          border-radius: 12px 0 8px 0;
          padding: 0 8px;

          &:before {
            content: '';
            position: relative;
            display: inline-block;
            margin-right: 3px;
            @include sprite-item('qbresult/collection');
          }
        }

        .video-player-progress {
          position: absolute;
          bottom: 0;
          z-index: 1;
          width: 100%;
          height: 4px;
          background: rgba(255, 255, 255, 0.5);

          &-played {
            position: absolute;
            height: 100%;
            background: rgba(19,108,233,1);
          }
        }
      }

      .qb-video-detail {
        display: flex;
        color: #999;
        text-overflow: ellipsis;
        word-wrap: normal;
        white-space: nowrap;
        align-items: center;
        height: 16px;
        margin-top: 8px;


        .video-detail-sitename, .video-detail-date {
          height: 16px;
          line-height: 14px;
          font-size: 14px;
          font-family: "PingFang SC";
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .video-detail-date {
          margin-left: 8px;
        }
      }
    }
  }
}
</style>
